import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  FilteringState,
  IntegratedFiltering,
  IntegratedSorting,
  SelectionState,
  SortingState,
  TableColumnVisibility,
} from '@devexpress/dx-react-grid';
import {useWindowResize} from '@ozgene/gp5.hooks.use-window-resize';
import {
  Grid,
  VirtualTable,
  TableHeaderRow,
  TableSelection,
  TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import {ColumnDefinedFilterCell} from 'util/components/ColumnDefinedFilterCell';
import {CustomRowComponent} from 'util/components/CustomRowComponent';
import {CustomNoDataCell} from 'util/components/CustomNoDataCell';
import {ColumnDefinedCell} from 'util/components/ColumnDefinedCell';
import ChipProvider from 'util/components/ChipProvider';
import {useStyles} from 'util/Styles';
import DateProvider from 'util/components/DateProvider';

function ImportedProjectList(props) {
  const {
    projects,
    onSelectionChange,
    projectFilterText,
    userFilterText,
    onChangeUserFilterText,
    onChangeProjectFilterText,
    selectedId,
  } = props;
  const {height} = useWindowResize();
  const [filters, setFilters] = useState([
    projectFilterText.length > 0 && {
      columnName: 'name',
      operation: 'contains',
      value: projectFilterText,
    },
    userFilterText.length > 0 && {
      columnName: 'users',
      operation: 'contains',
      value: userFilterText,
    },
  ]);
  const classes = useStyles();
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const projectFilter = newFilters.find(({columnName}) => columnName === 'name');
      const userFilter = newFilters.find(({columnName}) => columnName === 'users');
      const projectFilterValue = projectFilter?.value ?? '';
      const userFilterValue = userFilter?.value ?? '';

      if (projectFilterValue !== projectFilterText) {
        onChangeProjectFilterText(projectFilterValue);
      }

      if (userFilterValue !== userFilterText) {
        onChangeUserFilterText(userFilterValue);
      }

      setFilters(newFilters);
    },
    [onChangeProjectFilterText, onChangeUserFilterText, projectFilterText, userFilterText],
  );
  const ProjectTableColumns = [
    {
      name: 'name',
      title: 'Name',
      getCellValue: (row) => row.cnsObject.name,
    },
    {
      name: 'users',
      title: 'Related Users',
      getCellValue: (row) =>
        row.users.map((user) => {
          return {text: user.fullName, color: 'primary', size: 'medium', className: classes.chip};
        }),
    },
    {
      name: 'createdDt',
      title: 'Date Created',
      getCellValue: (row) => row.createdDt,
    },
  ];
  const columnFilters = [
    {
      columnName: 'users',
      predicate: (value, filter) =>
        value.some((user) =>
          user.text.toUpperCase().includes(filter.value.toString().toUpperCase().trim()),
        ),
    },
  ];
  const columnExtensions = [
    {
      columnName: 'createdDt',
      wordWrapEnabled: true,
    },
    {
      columnName: 'users',
      wordWrapEnabled: true,
    },
    {
      columnName: 'name',
      wordWrapEnabled: true,
    },
  ];

  return (
    <Grid rows={projects} columns={ProjectTableColumns} getRowId={({id}) => id}>
      <FilteringState filters={filters} onFiltersChange={handleFiltersChange} />
      <SortingState defaultSorting={[{columnName: 'createdDt', direction: 'desc'}]} />
      <SelectionState selection={[selectedId]} onSelectionChange={onSelectionChange} />
      <IntegratedFiltering columnExtensions={columnFilters} />
      <IntegratedSorting />

      <ChipProvider for={['users']} />
      <DateProvider for={['createdDt']} />
      <VirtualTable
        height={height - 140}
        cellComponent={ColumnDefinedCell}
        noDataCellComponent={CustomNoDataCell}
        columnExtensions={columnExtensions}
      />
      <TableColumnVisibility hiddenColumnNames={['createdDt']} />
      <TableHeaderRow showSortingControls />
      <TableSelection
        selectByRowClick
        highlightRow
        showSelectionColumn={false}
        rowComponent={CustomRowComponent}
      />
      <TableFilterRow showFilterSelector cellComponent={ColumnDefinedFilterCell} />
    </Grid>
  );
}

ImportedProjectList.propTypes = {
  projects: PropTypes.array.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  projectFilterText: PropTypes.string.isRequired,
  userFilterText: PropTypes.string.isRequired,
  onChangeUserFilterText: PropTypes.func.isRequired,
  onChangeProjectFilterText: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
};

export default ImportedProjectList;
